// ─────────────────────────────────────────────────────────────────────────────
// UsersTable.js — the directory list view. Clicking a row opens the user detail
// modal (via onRowClick); there is no per-row action menu.
//   SESSION maps to a live node region when the API exposes one; there is no
//   session/region field on the user list today, so it falls back to a dash
//   (matching how the design shows "—" for users without an active session).
// ─────────────────────────────────────────────────────────────────────────────
import styles from "./UsersTable.module.css";
import { userInitials, userName, formatRelative } from "../../../services/usersService";
import { StatusBadge } from "./badges";

const COLUMNS = ["USER", "EMAIL", "TEAM", "ROLE", "STATUS", "SESSION", "LAST ACTIVITY"];
// trailing actions column has no header label (matches the Figma)
const COL_COUNT = COLUMNS.length + 1;

// three-dots (kebab) action glyph, per the Figma row action
const KebabIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <circle cx="8" cy="8" r="1.2"/><circle cx="12.7" cy="8" r="1.2"/><circle cx="3.3" cy="8" r="1.2"/>
  </svg>
);

// Read a live region if the API ever provides one, else null → "—".
const sessionRegion = (u) =>
  u.session || u.activeSession?.nodeRegion || u.sessionRegion || u.nodeRegion || null;

export default function UsersTable({ rows, loading, onRowClick }) {
  return (
    <div className={styles.wrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            {COLUMNS.map((label) => (
              <th key={label} className={styles.th}>{label}</th>
            ))}
            <th className={`${styles.th} ${styles.thActions}`} aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: 7 }).map((_, i) => (
              <tr key={i} className={styles.tr}>
                {Array.from({ length: COL_COUNT }).map((__, j) => (
                  <td key={j} className={styles.td}><span className={styles.skeleton} /></td>
                ))}
              </tr>
            ))
          ) : rows.length > 0 ? (
            rows.map((user) => {
              const region = sessionRegion(user);
              return (
                <tr
                  key={user.id}
                  className={styles.tr}
                  onClick={() => onRowClick?.(user)}
                >
                  <td className={styles.td}>
                    <div className={styles.userCell}>
                      <div className={styles.avatar}>{userInitials(user)}</div>
                      <span className={styles.userName}>{userName(user)}</span>
                    </div>
                  </td>
                  <td className={`${styles.td} ${styles.tdEmail}`}>{user.email}</td>
                  <td className={`${styles.td} ${styles.tdText}`}>{user.team || "—"}</td>
                  <td className={`${styles.td} ${styles.tdText}`}>{user.role}</td>
                  <td className={styles.td}><StatusBadge status={user.status} /></td>
                  <td className={styles.td}>
                    {region
                      ? <span className={styles.sessionTag}>{region}</span>
                      : <span className={styles.sessionNone}>-</span>}
                  </td>
                  <td className={`${styles.td} ${styles.tdTime}`}>
                    {formatRelative(user.lastActiveAt)}
                  </td>
                  <td className={`${styles.td} ${styles.tdActions}`}>
                    <span className={styles.actionsBtn} aria-hidden="true"><KebabIcon /></span>
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={COL_COUNT} className={styles.emptyRow}>
                No users match your filters.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
